export interface UploadDate {
  $date: string;
}

export interface UpdateDate {
  $date: string;
}
/** Project/account domain from registration or API (not a fixed frontend list). */
export type Domain = string;
export type UploadType = 'ML_MODEL' | 'LLM_MODEL' | 'DATA_SET' | 'POLICY_DOCUMENT' | 'VIDEO' | 'AUDIO' | 'LOGS';

/** Backend default for POLICY_MAX_FILES_PER_UPLOAD */
export const POLICY_MAX_FILES_PER_UPLOAD = 10;

export interface FileUploadInput {
  file?: File;
  files?: File[];
  domain: Domain | undefined | null;
  uploadType: UploadType;
  targetColumn?: string;
  projectId: string;
  userId: string;
  title?: string;
  organizationId?: string;
}

export interface PolicyUploadAccepted {
  upload_id: string;
  file_name: string;
  chunk_count: number;
  policy_processing_status: string;
  rag_enabled: boolean;
}

export interface PolicyUploadFailed {
  file_name: string;
  error: string;
}

export interface PolicyDocumentUploadResponse {
  status: number;
  message: string;
  projectId?: string;
  organization_id?: string;
  domain?: string;
  accepted_count: number;
  failed_count: number;
  accepted_uploads: PolicyUploadAccepted[];
  failed_uploads: PolicyUploadFailed[];
}

interface DatasetSchema {
  file_name: string;
  upload_date: string;
  update_date: string;
  upload_type: string;
  num_rows: number;
  num_columns: number;
  missing_values: Record<string, number>; // Adjust the type based on the structure of `missing_values`.
  sensitive_attributes: string[]; // Assuming the array contains strings. Adjust the type if necessary.
  target_column: string | null;
  preprocessed: boolean;
  projectId: string;
  userId: string;
}

interface ModelSchema {
  file_name: string;
  upload_date: string; // You can change this to `Date` if you are working with Date objects.
  update_date: string; // Same as above.
  upload_type: string;
  model_metadata: never;
  projectId: string;
  userId: string;
}
interface ModelMetadata {
  model: string;
  temperature: number;
  max_tokens: number;
  examples: string[];
  prompt: string;
}

interface LLMModelSchema {
  file_url: string;
  upload_date: string; // Consider changing this to `Date` if you'll be working with Date objects.
  update_date: string; // Same as above.
  upload_type: string;
  model_metadata: ModelMetadata;
  projectId: string;
  userId: string;
}

interface PolicyDocumentSchema {
  file_url: string;
  upload_date: string; // Consider changing this to `Date` if you'll be working with Date objects.
  update_date: string; // Same as above.
  upload_type: string;
  extracted_metadata: string; // Assuming this is a string based on your data, otherwise, create a structured interface.
  projectId: string;
  userId: string;
  domain: string;
}
export interface Uploads {
  ml_model: ModelSchema[];
  llm_model: LLMModelSchema[];
  policy_document: PolicyDocumentSchema[];
  dataset: DatasetSchema[];
}
export interface ProjectCreateRequestInput {
  userId: string;
  title: string;
  domain?: Domain;
  overview: string;
  environment?: string;
  organizationId: string;
}

export interface ProjectUpdateRequestInput extends ProjectCreateRequestInput {
  projectId: string;
  userId: string;
  title: string;
  domain: Domain;
  overview: string;
  organizationId: string;
}

export type ATACProjectStatus = 'draft' | 'in_progress' | 'assessed' | 'certified';

export interface Project extends ProjectCreateRequestInput {
  userId: string;
  projectId: string;
  uploads?: Uploads;
  createdOn?: string;
  atacStatus?: ATACProjectStatus;
  provider?: string;
  modelName?: string;
  projectStatus?: 'COMPLETED' | 'PENDING' | 'FAILED' | 'IN_PROGRESS';
  feature_results?: FeatureResults;
  whitelist_rules?: WhitelistRule[];
  review_status?: string;
  reviewStatus?: string;
}

export interface WhitelistRule {
  [key: string]: {
    setA: string[];
    setB: string[];
  };
}
interface FeatureResults {
  llm_results: Result[];
  ml_results: Result[];
}

export interface ChartData {
  xAxis: string[];
  yAxis: number[];
}

export interface Result {
  Action: string;
  Attribute: string;
  'Bias Detected': boolean;
  'Disparity Ratio': number;
  'External Compliance': string;
  'Internal Policy Compliance': string;
  'Polynomial Calculation': string;
  Risk: string;
}

export interface CreateProjectResponse {
  message: string;
  projectId: string;
  domain: Domain;
}

export type ProjectList = Project[];

export type ProjectInput = {
  title: string;
  domain: string;
  overview: string;
};

// Unique ID Object
interface ObjectId {
  $oid: string;
}

// Bias Data Interface
export interface BiasData {
  Attribute: string;
  'Bias Detected': boolean;
  'Disparity Ratio': number;
  'Calculated Impact': number;
  'External Compliance': string;
  'Internal Policy Compliance': string;
  Risk: string;
  Action: string;
  'Bias Drift': string;
  'Intersectional Bias': string;
}

// Fairness Data Interface
export interface FairnessData {
  Attribute: string;
  'Disparity Ratio': number;
  'Equal Opportunity': number;
  Risk: string;
}

// Bias Section (ML & LLM)
export interface Bias {
  ml_bias_data: BiasData[];
  llm_bias_data: BiasData[];
}

// Fairness Section (ML & LLM)
export interface Fairness {
  fairness_data_ml: FairnessData[];
  fairness_data_llm: FairnessData[];
  overall_risk_score_ml: number;
  overall_risk_score_llm: number;
}

// Results Interface
export interface ProjectResults {
  bias: Bias;
  fairness: Fairness;
}

// Main Project Results Response Interface
export interface ProjectResultsResponse {
  _id: ObjectId;
  projectId: string;
  results: ProjectResults;
}
